import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import RolesList from './RolesList';
import UsersList from './UsersList';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, addToast, removeToast, showSuccess, showError, showWarning } = useToast();
  const { hasPermission, userRole, permissions, isLoading: permissionLoading } = usePermissionContext();

  // All hooks must be called before any conditional statements
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'users');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Check if user has access to current active tab, if not switch to first available tab
  useEffect(() => {
    if (!permissionLoading) {
      console.log('🔐 RBAC DEBUG - Checking access to active tab:', activeTab);

      if (activeTab === 'users' && !hasPermission('user.view')) {
        console.log('🔐 RBAC DEBUG - User does not have user.view permission, switching tab');
        if (hasPermission('role.view')) {
          setActiveTab('roles');
        } else if (hasPermission('sales_commission_agent.view')) {
          setActiveTab('agents');
        }
      } else if (activeTab === 'roles' && !hasPermission('role.view')) {
        console.log('🔐 RBAC DEBUG - User does not have role.view permission, switching tab');
        if (hasPermission('user.view')) {
          setActiveTab('users');
        } else if (hasPermission('sales_commission_agent.view')) {
          setActiveTab('agents');
        }
      } else if (activeTab === 'agents' && !hasPermission('sales_commission_agent.view')) {
        console.log('🔐 RBAC DEBUG - User does not have sales_commission_agent.view permission, switching tab');
        if (hasPermission('user.view')) {
          setActiveTab('users');
        } else if (hasPermission('role.view')) {
          setActiveTab('roles');
        }
      }
    }
  }, [permissionLoading, activeTab, hasPermission]);

  // Load data when active tab changes
  useEffect(() => {
    if (!permissionLoading) {
      loadData(activeTab);
    }
  }, [activeTab, permissionLoading]);

  // Listen for navigation state changes to detect when returning from user form
  useEffect(() => {
    if (location.state?.userCreated) {
      showSuccess('User created successfully!');
      // Clear the state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.userUpdated) {
      showSuccess('User updated successfully!');
      // Clear the state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, showSuccess]);

  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = data.filter(item => {
      if (activeTab === 'users') {
        const matchesSearch =
          (item.username || '').toLowerCase().includes(lowerSearch) ||
          (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`).toLowerCase().includes(lowerSearch) ||
          (item.email || '').toLowerCase().includes(lowerSearch);

        const roleName = (item.role?.name || item.role_name || item.role || '').toLowerCase();
        const matchesRole = filterRole === 'all' || roleName === filterRole.toLowerCase();

        let isActive = item.status === 'active' || item.is_active === true || item.is_active === 1;
        const matchesStatus =
          filterStatus === 'all' ||
          (filterStatus === 'active' && isActive) ||
          (filterStatus === 'inactive' && !isActive);

        return matchesSearch && matchesRole && matchesStatus;
      } else if (activeTab === 'roles') {
        return (item.name || '').toLowerCase().includes(lowerSearch);
      } else if (activeTab === 'agents') {
        return (item.name || '').toLowerCase().includes(lowerSearch) ||
          (item.email || '').toLowerCase().includes(lowerSearch);
      }
      return false;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // reset to first page on filter change
  }, [searchTerm, filterRole, filterStatus, data, activeTab]);

  // Show loading state while permissions are loading
  if (permissionLoading) {
    console.log('🔐 RBAC DEBUG - Still loading permissions, showing loading state');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading permissions...</div>
        <div style={{ fontSize: '14px', color: '#999' }}>Please wait while we set up your access rights</div>
      </div>
    );
  }

  const handleExport = (format) => {
    if (!filteredData || filteredData.length === 0) {
      showWarning('No data to export');
      setShowExportDropdown(false);
      return;
    }

    let headers = [];
    let rows = [];
    let title = '';
    let filename = '';

    if (activeTab === 'users') {
      headers = ['Username', 'Full Name', 'Role', 'Email', 'Status'];
      rows = filteredData.map(item => [
        item.username || '',
        item.full_name || `${item.first_name || ''} ${item.last_name || ''}`,
        item.role?.name || item.role_name || item.role || '',
        item.email || '',
        (item.status === 'active' || item.is_active) ? 'Active' : 'Inactive'
      ]);
      title = 'Users List';
      filename = 'users';
    } else if (activeTab === 'roles') {
      headers = ['Role Name', 'Permissions Count', 'Users Count', 'Type'];
      rows = filteredData.map(item => [
        item.name || '',
        item.permissions?.length || 0,
        item.users_count || 0,
        item.is_default ? 'Default' : 'Custom'
      ]);
      title = 'Roles List';
      filename = 'roles';
    } else if (activeTab === 'agents') {
      headers = ['Name', 'Email', 'Contact Number', 'Address', 'Commission %'];
      rows = filteredData.map(item => [
        item.name || '',
        item.email || '',
        item.contact_no || '',
        item.address || '',
        `${item.cmmsn_percent || 0}%`
      ]);
      title = 'Sales Commission Agents';
      filename = 'agents';
    }

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { showWarning('Column visibility settings'); setShowExportDropdown(false); };


  // Pagination state is now declared at the top of the component

  const loadData = async (tab) => {
    setIsLoading(true);
    try {
      let result = [];
      if (tab === 'users') {
        const userDetails = await apiService.getUserDetails();
        const businessId = userDetails?.business_id || 40;

        const res = await apiService.getUsers({
          business_id: businessId,
          per_page: -1 // Get all users
        });
        result = res.data || res || [];
      } else if (tab === 'roles') {
        // Roles are handled by RolesList component
        result = [];
      } else if (tab === 'agents') {
        const res = await apiService.getSalesCommissionAgents();
        result = res.data || res || [];
      }
      setData(result);
      setFilteredData(result);
    } catch (error) {
      console.error(`Error loading ${tab}:`, error);
      setData([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRecordDetails = () => {
    if (!selectedRecord) return null;

    if (activeTab === 'users') {
      return (
        <div className="record-details">
          <div><strong>Username:</strong> {selectedRecord.username || 'N/A'}</div>
          <div><strong>Full name:</strong> {selectedRecord.full_name || `${selectedRecord.first_name || ''} ${selectedRecord.last_name || ''}`}</div>
          <div><strong>Role:</strong> {selectedRecord.role || 'N/A'}</div>
          <div><strong>Email:</strong> {selectedRecord.email || 'N/A'}</div>
          <div><strong>Contact:</strong> {selectedRecord.contact_number || 'N/A'}</div>
          <div><strong>Status:</strong> {selectedRecord.status || 'Active'}</div>
        </div>
      );
    }

    if (activeTab === 'roles') {
      return (
        <div className="record-details">
          <div><strong>Role name:</strong> {selectedRecord.name || 'N/A'}</div>
          <div><strong>Description:</strong> {selectedRecord.description || 'N/A'}</div>
        </div>
      );
    }

    if (activeTab === 'agents') {
      return (
        <div className="record-details">
          <div><strong>Name:</strong> {selectedRecord.name || 'N/A'}</div>
          <div><strong>Email:</strong> {selectedRecord.email || 'N/A'}</div>
          <div><strong>Contact number:</strong> {selectedRecord.contact_no || 'N/A'}</div>
          <div><strong>Address:</strong> {selectedRecord.address || 'N/A'}</div>
          <div><strong>Commission %:</strong> {selectedRecord.cmmsn_percent || 0}</div>
        </div>
      );
    }

    return null;
  };

  // These useEffect hooks have been moved to the top of the component

  // This useEffect has been moved to the top of the component

  // Calculate Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const toggleActionMenu = (id) => {
    setOpenActionId(openActionId === id ? null : id);
  };

  const handleViewRecord = (record) => {
    if (!record) return;
    setSelectedRecord(record);
    setShowViewModal(true);
    setOpenActionId(null);
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
  };

  const handleAdd = () => {
    console.log('🔐 RBAC DEBUG - handleAdd called for tab:', activeTab);

    if (activeTab === 'users') {
      console.log('🔐 RBAC DEBUG - Checking user.create permission');
      // Check if user has permission to create users
      if (!hasPermission('user.create')) {
        console.log('🔐 RBAC DEBUG - user.create permission denied');
        showWarning('You do not have permission to create users');
        return;
      }
      console.log('🔐 RBAC DEBUG - user.create permission granted, navigating to add user');
      navigate('/users/add');
    } else if (activeTab === 'roles') {
      console.log('🔐 RBAC DEBUG - Checking role.create permission');
      // Check if user has permission to create roles
      if (!hasPermission('role.create')) {
        console.log('🔐 RBAC DEBUG - role.create permission denied');
        showWarning('You do not have permission to create roles');
        return;
      }
      console.log('🔐 RBAC DEBUG - role.create permission granted, navigating to add role');
      navigate('/roles/add');
    } else if (activeTab === 'agents') {
      console.log('🔐 RBAC DEBUG - Checking sales_commission_agent.create permission');
      // Check if user has permission to create sales commission agents
      if (!hasPermission('sales_commission_agent.create')) {
        console.log('🔐 RBAC DEBUG - sales_commission_agent.create permission denied');
        showWarning('You do not have permission to create sales commission agents');
        return;
      }
      console.log('🔐 RBAC DEBUG - sales_commission_agent.create permission granted, navigating to add agent');
      navigate('/sales-commission-agents/add');
    }
  };

  const renderTable = () => {
    // Show loading state
    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          minHeight: '300px'
        }}>
          <div style={{
            fontSize: '24px',
            marginBottom: '12px',
            color: '#64748b'
          }}>
            ⏳ Loading {activeTab === 'users' ? 'Users' : activeTab === 'roles' ? 'Roles' : 'Sales Commission Agents'}...
          </div>
          <div style={{
            fontSize: '14px',
            color: '#94a3b8'
          }}>
            Fetching {activeTab === 'users' ? 'users' : activeTab === 'roles' ? 'roles' : 'agents'} data from server...
          </div>
        </div>
      );
    }

    if (activeTab === 'users') {
      return (
        <table className="products-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Role</th>
              <th>Email</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.full_name || `${user.first_name || ''} ${user.last_name || ''}`}</td>
                <td>{user.role}</td>
                <td>{user.email}</td>
                <td className="actions-cell">
                  <div className="action-dropdown-container">
                    <button
                      className="action-btn-trigger"
                      onClick={() => toggleActionMenu(user.id)}
                    >
                      Actions <span className="arrow-down">▼</span>
                    </button>
                    {openActionId === user.id && (
                      <div className="action-dropdown-menu">
                        <button onClick={() => handleViewRecord(user)}>View</button>
                        <button onClick={() => console.log('Edit', user.id)}>Edit</button>
                        <button onClick={() => console.log('Delete', user.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (activeTab === 'roles') {
      return (
        <table className="products-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(role => (
              <tr key={role.id}>
                <td>{role.name}</td>
                <td className="actions-cell">
                  <div className="action-dropdown-container">
                    <button
                      className="action-btn-trigger"
                      onClick={() => toggleActionMenu(role.id)}
                    >
                      Actions <span className="arrow-down">▼</span>
                    </button>
                    {openActionId === role.id && (
                      <div className="action-dropdown-menu">
                        <button onClick={() => handleViewRecord(role)}>View</button>
                        <button onClick={() => console.log('Edit', role.id)}>Edit</button>
                        <button onClick={() => console.log('Delete', role.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (activeTab === 'agents') {
      return (
        <table className="products-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Name</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Address</th>
              <th>Commission %</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(agent => (
              <tr key={agent.id}>
                <td className="actions-cell">
                  <div className="action-dropdown-container">
                    <button
                      className="action-btn-trigger"
                      onClick={() => toggleActionMenu(agent.id)}
                    >
                      Actions <span className="arrow-down">▼</span>
                    </button>
                    {openActionId === agent.id && (
                      <div className="action-dropdown-menu">
                        <button onClick={() => handleViewRecord(agent)}>View</button>
                        <button onClick={() => console.log('Edit', agent.id)}>Edit</button>
                        <button onClick={() => console.log('Delete', agent.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                </td>
                <td>{agent.name}</td>
                <td>{agent.email}</td>
                <td>{agent.contact_no}</td>
                <td>{agent.address}</td>
                <td>{agent.cmmsn_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  };

  return (
    <div className="products-container">
      {/* Main Card: Tabs + Table Controls + Table */}
      <div className="main-card">
        <div className="product-tabs">
          {/* Show all tabs if permissions array is empty (fallback behavior) */}
          {(permissions.length === 0 || hasPermission('user.view')) && (
            <div
              className={`product-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <i className="fa fa-users"></i> Users
            </div>
          )}
          {(permissions.length === 0 || hasPermission('role.view')) && (
            <div
              className={`product-tab ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              <i className="fa fa-id-badge"></i> Roles
            </div>
          )}
          {(permissions.length === 0 || hasPermission('sales_commission_agent.view')) && (
            <div
              className={`product-tab ${activeTab === 'agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('agents')}
            >
              <i className="fa fa-user-tie"></i> Sales Commission Agents
            </div>
          )}
          </div>

        {/* Table Controls Row */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            {(() => {
              console.log('🔐 RBAC DEBUG - Checking Add button visibility for tab:', activeTab);
              const canCreateUsers = activeTab === 'users' && (permissions.length === 0 || hasPermission('user.create'));
              const canCreateRoles = activeTab === 'roles' && (permissions.length === 0 || hasPermission('role.create'));
              const canCreateAgents = activeTab === 'agents' && (permissions.length === 0 || hasPermission('sales_commission_agent.create'));

              console.log('🔐 RBAC DEBUG - Can create users:', canCreateUsers);
              console.log('🔐 RBAC DEBUG - Can create roles:', canCreateRoles);
              console.log('🔐 RBAC DEBUG - Can create agents:', canCreateAgents);
              console.log('🔐 RBAC DEBUG - Show Add button:', canCreateUsers || canCreateRoles || canCreateAgents);

              return canCreateUsers || canCreateRoles || canCreateAgents;
            })() && (
                <button className="control-btn-primary" onClick={handleAdd}>
                  <span className="plus-icon">+</span> Add
                </button>
              )}
          </div>
        </div>

        {/* Data Table */}
        <div className="products-table-container">
          {activeTab === 'users' ? (
            <UsersList />
          ) : activeTab === 'roles' ? (
            <RolesList />
          ) : isLoading ? (
            <div className="loading-sales">
              <h3>⏳ Loading Sales Commission Agents...</h3>
              <p>Fetching sales commission agents data from server...</p>
            </div>
          ) : filteredData.length > 0 ? (
            renderTable()
          ) : (
            <div className="loading-sales" style={{ textAlign: 'center' }}>
              <h3>📊 No agents found</h3>
              <p>Try adjusting filters or add a new agent.</p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {filteredData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  backgroundColor: '#9facf5', // Light indigo/purple
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.2rem',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.8 : 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  fontSize: '0.9rem'
                }}
              >
                Previous
              </button>

              <span style={{ color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>
                Page {currentPage} of {totalPages || 1}
              </span>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  backgroundColor: '#3b82f6', // Bright Blue
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.7 : 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  fontSize: '0.9rem'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {showViewModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: '520px', width: '95%' }}>
            <div className="modal-header">
              <h2>View {activeTab === 'users' ? 'User' : activeTab === 'roles' ? 'Role' : 'Agent'}</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              {renderRecordDetails()}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                {activeTab === 'users' && (
                  <>
                    <div className="filter-group">
                      <label>Role:</label>
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                      >
                        <option value="all">All Roles</option>
                        {/* Populate from distinct roles in data */}
                        {[...new Set(data.map(u => u.role?.name || u.role_name || u.role).filter(Boolean))].sort().map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Status:</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </>
                )}
                {activeTab === 'roles' && (
                  <div className="filter-group">
                    <label>Search Role:</label>
                    <input
                      type="text"
                      placeholder="Role Name"
                      className="form-input"
                      style={{ padding: '0.6rem' }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}
                {activeTab === 'agents' && (
                  <div className="filter-group">
                    <label>Contact Number:</label>
                    <input type="text" placeholder="Contact" className="form-input" style={{ padding: '0.6rem' }} />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn close-btn"
                onClick={() => { setFilterRole('all'); setFilterStatus('all'); setSearchTerm(''); }}
                style={{ marginRight: 'auto' }}
              >
                Reset Filters
              </button>
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default Users;
